// SPDX-License-Identifier: Apache-2.0

// tether-brain v2 — VaultStore (in-memory + Dexie-backed).
//
// Public surface, per the gift summary:
//   init() · addDoc(file) · updateDoc(id, content) · removeDoc(id)
//   searchDocs(query, k=10) · getDoc(id) · listDocs(filter?)
//
// Reactivity: Solid-friendly. We expose a `signal()` accessor that returns
// the current doc list as a plain array, plus a `subscribe(cb)` for callers
// who can't import solid-js (storage is meant to be UI-agnostic). Solid
// components should wrap `store.signal` in `createSignal(store.signal())` and
// subscribe inside an effect — see the snippet at the bottom of this file.
//
// Concurrency model: every Dexie write goes through a single async path, so
// concurrent `addDoc` calls don't tear the in-memory mirror. We rebuild the
// TF-IDF index lazily — first search after a write triggers it, so a
// drag-and-drop of 50 files doesn't rebuild 50 times.

import { getDB, type TetherDB } from './dexie-schema';
import {
  parseFile,
  parseFrontmatter,
  type IngestResult,
} from './ingest';
import {
  buildTfidfIndex,
  queryTfidf,
} from './tfidf';
import {
  embedDocs as embedDocsImpl,
  removeDocEmbeddings,
} from './embeddings';
import {
  bindDocToYjs,
  flushDocYjs,
  unbindDoc,
  readYDocContent,
} from './yjs-binding';
import type {
  SearchHit,
  Tier,
  VaultDoc,
  VaultFrontmatter,
  VaultTint,
} from './schema';

// ─── helpers ────────────────────────────────────────────────────────────────

const TINT_CYCLE: VaultTint[] = ['violet', 'cyan', 'amber', 'rose'];

function makeId(): string {
  return (
    Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4)
  );
}

function pickTint(idx: number): VaultTint {
  return TINT_CYCLE[idx % TINT_CYCLE.length] ?? 'violet';
}

// ─── filter / list ──────────────────────────────────────────────────────────

export interface ListFilter {
  /** Restrict to docs whose frontmatter sensitivity matches one of these. */
  sensitivity?: Tier | Tier[];
  /** Restrict to docs whose frontmatter type matches. */
  type?: VaultFrontmatter['type'];
  /** Restrict to docs whose frontmatter slot matches. */
  slot?: string;
  /** Substring match against title (case-insensitive). */
  titleLike?: string;
}

function passesFilter(doc: VaultDoc, f: ListFilter | undefined): boolean {
  if (!f) return true;
  if (f.sensitivity) {
    const wanted = Array.isArray(f.sensitivity)
      ? f.sensitivity
      : [f.sensitivity];
    if (!wanted.includes(doc.front.sensitivity ?? 'internal')) return false;
  }
  if (f.type && doc.front.type !== f.type) return false;
  if (f.slot && doc.front.slot !== f.slot) return false;
  if (
    f.titleLike &&
    !doc.title.toLowerCase().includes(f.titleLike.toLowerCase())
  ) {
    return false;
  }
  return true;
}

// ─── store ──────────────────────────────────────────────────────────────────

export interface VaultStoreOptions {
  /** Override DB instance (tests pass a named DB here). */
  db?: TetherDB;
  /** If true, skip the TF-IDF rebuild on init — call `rebuildSearchIndex()`
   *  manually. Default false. */
  deferIndex?: boolean;
}

export class VaultStore {
  private db: TetherDB;
  private docs: Map<string, VaultDoc> = new Map();
  private listeners: Set<() => void> = new Set();
  private indexDirty = true;
  private inited = false;
  /** Coalesces concurrent index rebuilds into one. */
  private indexBuildPromise: Promise<void> | null = null;

  constructor(opts: VaultStoreOptions = {}) {
    this.db = opts.db ?? getDB();
  }

  /** Load existing docs from Dexie. Idempotent. */
  async init(): Promise<void> {
    if (this.inited) return;
    const all = await this.db.docs.toArray();
    this.docs = new Map(all.map((d) => [d.id, d]));
    this.inited = true;
    this.indexDirty = true;
    this.emit();
  }

  // ─── reactivity ───────────────────────────────────────────────────────────

  /** Returns the current array of docs (sorted by `updatedAt` desc). Cheap
   *  enough to call from a Solid `createMemo`. */
  signal(): VaultDoc[] {
    return Array.from(this.docs.values()).sort(
      (a, b) => b.updatedAt - a.updatedAt,
    );
  }

  /** Subscribe to mutations. Returns an unsubscribe function. */
  subscribe(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private emit(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch {
        /* swallow — a buggy listener shouldn't break the store */
      }
    }
  }

  // ─── CRUD ────────────────────────────────────────────────────────────────

  /** Add a doc from an ingested file (or a pre-parsed result). Returns the
   *  freshly created doc. */
  async addDoc(input: File | IngestResult): Promise<VaultDoc | null> {
    const ingested = await this.normalizeInput(input);
    if (!ingested) return null;

    const id = makeId();
    const now = Date.now();
    const tint = pickTint(this.docs.size);
    const doc: VaultDoc = {
      id,
      title: ingested.title,
      content: ingested.content,
      front: ingested.front,
      tint,
      createdAt: now,
      updatedAt: now,
      bytes: ingested.content.length,
      ...(ingested.path !== undefined ? { path: ingested.path } : {}),
    };
    await this.db.docs.put(doc);
    this.docs.set(id, doc);
    this.indexDirty = true;
    this.emit();
    return doc;
  }

  /** Add a doc without going through ingest — used by SPA "+ New" button. */
  async createBlankDoc(title = 'untitled.md'): Promise<VaultDoc> {
    const id = makeId();
    const now = Date.now();
    const tint = pickTint(this.docs.size);
    const doc: VaultDoc = {
      id,
      title,
      content: `# ${title.replace(/\.md$/, '')}\n\n`,
      front: {},
      tint,
      createdAt: now,
      updatedAt: now,
      bytes: 0,
    };
    await this.db.docs.put(doc);
    this.docs.set(id, doc);
    this.indexDirty = true;
    this.emit();
    return doc;
  }

  async updateDoc(
    id: string,
    patch: Partial<Pick<VaultDoc, 'title' | 'content'>>,
  ): Promise<VaultDoc | null> {
    const existing = this.docs.get(id);
    if (!existing) return null;
    let nextFront = existing.front;
    let nextContent = existing.content;
    if (patch.content !== undefined) {
      // Re-parse frontmatter when content updates so the row stays in sync.
      const { front, body } = parseFrontmatter(patch.content);
      nextFront = { ...existing.front, ...front };
      nextContent = body;
    }
    const next: VaultDoc = {
      ...existing,
      title: patch.title ?? existing.title,
      content: nextContent,
      front: nextFront,
      updatedAt: Date.now(),
      bytes: nextContent.length,
    };
    await this.db.docs.put(next);
    this.docs.set(id, next);
    this.indexDirty = true;
    this.emit();
    return next;
  }

  /** Sync the doc's content from its bound Y.Doc — useful before serializing
   *  or before the user navigates away. No-op if the doc isn't bound. */
  async syncFromYjs(id: string): Promise<void> {
    await flushDocYjs(id);
    // Pull current content out of the Yjs binding (without touching the DB
    // row's `front`, since the editor edits body only).
    const yDoc = await bindDocToYjs(id, this.docs.get(id));
    const text = readYDocContent(yDoc);
    const existing = this.docs.get(id);
    if (!existing) return;
    if (existing.content === text) return;
    const next: VaultDoc = {
      ...existing,
      content: text,
      updatedAt: Date.now(),
      bytes: text.length,
    };
    await this.db.docs.put(next);
    this.docs.set(id, next);
    this.indexDirty = true;
    this.emit();
  }

  async removeDoc(id: string): Promise<boolean> {
    const existed = this.docs.has(id);
    if (!existed) return false;
    await this.db.transaction(
      'rw',
      [
        this.db.docs,
        this.db.embeddings_int8,
        this.db.yjs_snapshot,
        this.db.edges,
        this.db.adjacency_cache,
      ],
      async () => {
        await this.db.docs.delete(id);
        await this.db.embeddings_int8.where('docId').equals(id).delete();
        await this.db.yjs_snapshot.delete(id);
        await this.db.edges.where('a').equals(id).delete();
        await this.db.edges.where('b').equals(id).delete();
        await this.db.adjacency_cache.where('docId').equals(id).delete();
      },
    );
    // belt-and-suspenders: surface delete is idempotent
    await removeDocEmbeddings(id).catch(() => undefined);
    await unbindDoc(id);
    this.docs.delete(id);
    this.indexDirty = true;
    this.emit();
    return true;
  }

  // ─── reads ───────────────────────────────────────────────────────────────

  getDoc(id: string): VaultDoc | undefined {
    return this.docs.get(id);
  }

  listDocs(filter?: ListFilter): VaultDoc[] {
    return this.signal().filter((d) => passesFilter(d, filter));
  }

  // ─── search ──────────────────────────────────────────────────────────────

  /**
   * CPU TF-IDF search for v1. The WGSL `cosineSim` + `topK` kernels swap in
   * later by replacing the body of this method; the public signature stays.
   */
  async searchDocs(query: string, k = 10): Promise<SearchHit[]> {
    if (this.indexDirty) await this.rebuildSearchIndex();
    const hits = await queryTfidf(query, k);
    const out: SearchHit[] = [];
    for (const h of hits) {
      const doc = this.docs.get(h.docId);
      if (!doc) continue;
      out.push({ doc, score: h.score, source: 'tfidf' });
    }
    return out;
  }

  /** Force a TF-IDF rebuild. Coalesced — concurrent callers share one build. */
  async rebuildSearchIndex(): Promise<void> {
    if (this.indexBuildPromise) return this.indexBuildPromise;
    const docs = Array.from(this.docs.values());
    this.indexBuildPromise = (async () => {
      try {
        await buildTfidfIndex(docs);
        this.indexDirty = false;
      } finally {
        this.indexBuildPromise = null;
      }
    })();
    return this.indexBuildPromise;
  }

  /** Trigger a full embedding pass over every doc (or `subset`). Optional —
   *  the SPA can call this on idle. Persists to Dexie; the WGSL search kernel
   *  picks them up on its next dispatch. */
  async embedAll(opts: {
    model?: string;
    skipFresh?: boolean;
    onProgress?: (done: number, total: number) => void;
    signal?: AbortSignal;
    subset?: VaultDoc[];
  } = {}): Promise<number> {
    const docs = opts.subset ?? Array.from(this.docs.values());
    return embedDocsImpl(docs, {
      ...(opts.model !== undefined ? { model: opts.model } : {}),
      ...(opts.skipFresh !== undefined ? { skipFresh: opts.skipFresh } : {}),
      ...(opts.onProgress !== undefined ? { onProgress: opts.onProgress } : {}),
      ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
    });
  }

  // ─── ingest ──────────────────────────────────────────────────────────────

  private async normalizeInput(
    input: File | IngestResult,
  ): Promise<IngestResult | null> {
    if (input instanceof File) return parseFile(input);
    return input;
  }
}

// ─── module-level singleton ────────────────────────────────────────────────
//
// Multi-workspace routing: when a workspace is registered + active, we hand
// out the per-workspace store from `WorkspacesManager.getStore(activeId)`.
// To dodge a circular import (workspaces.ts imports `VaultStore` from this
// file), the manager wires itself in via `setVaultStoreResolver()` at
// module-init time. Until that wiring lands (or in single-vault tests that
// never mount the manager), we fall back to the original lazy singleton
// over the legacy default-named DB.

let _store: VaultStore | null = null;

/**
 * Resolver injected by `workspaces.ts`. Returning `null` means "no active
 * workspace, fall back to the legacy default singleton". This keeps the
 * single-vault contract intact for any code path that touches the store
 * before workspaces.init() runs.
 */
type VaultStoreResolver = () => VaultStore | null;
let _resolver: VaultStoreResolver | null = null;

/** Internal — workspaces.ts calls this on module load to register itself. */
export function setVaultStoreResolver(resolver: VaultStoreResolver | null): void {
  _resolver = resolver;
}

export function getVaultStore(): VaultStore {
  if (_resolver) {
    try {
      const routed = _resolver();
      if (routed) return routed;
    } catch {
      /* fall through to single-vault path */
    }
  }
  if (!_store) _store = new VaultStore();
  return _store;
}

export function _resetStoreForTests(): void {
  _store = null;
  _resolver = null;
}

/*
 * ──── Solid integration sketch (kept here as a comment, not an export) ────
 *
 *   import { createSignal, onMount, onCleanup } from 'solid-js';
 *   import { getVaultStore } from '@tether/storage';
 *
 *   export function useVault() {
 *     const store = getVaultStore();
 *     const [docs, setDocs] = createSignal(store.signal());
 *     onMount(async () => {
 *       await store.init();
 *       setDocs(store.signal());
 *       const off = store.subscribe(() => setDocs(store.signal()));
 *       onCleanup(off);
 *     });
 *     return { docs, store };
 *   }
 *
 * The store doesn't import solid-js itself so it stays usable from MCP-side
 * tests, the future Service-Worker WebLLM path, and headless e2e specs.
 */
